import {
	Box,
	Icon,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContent } from "../../../shared/components";
import { useToast } from "../../../shared/hooks";
import { CreditCardOutlined } from "@mui/icons-material";
import { addDays, format, subDays } from "date-fns";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { ToolbarGridAnalises } from "../../../shared/components";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { useProfile } from "../../../context/ProfileContext";
import { Environment } from "../../../shared/environment";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useDebounce } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { analysisService, IanalysisList } from "../../../shared/services/api/analysis/analysisService";
import { globalColorText, globalMaskDocument, globalMaskReal } from "../../../shared/utils";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { ListParamsAnalysis } from "../types/listParams.type";
import { AnalysisStatusChip } from "./AnalysisStatusChip";
import { ToolbarAnalysis } from "./toobalAnalysis/ToolbarAnalysis";
import { LoadingComp } from "../../../shared/components/loading/Loading";

export const AnalysisListPresentation = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();
	const navigate = useNavigate();
	const [rows, setRows] = useState<IanalysisList[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const { isSuperAdmin } = useProfile();
	const hasViewPermission = useMemo(() => {
		return isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.AnalysisOptions, PermissionActionsEnum.View);
	}, [isSuperAdmin]);
	const toastHandler = useToast();
	const [pesquisar, setPesquisar] = useState(true);
	const today = new Date();

	const filterInitialParams: ListParamsAnalysis = useMemo(() => {
		return {
			tipo: "documento",
			status: "Todos",
			createFrom: "Todos",
			beginDate: format(subDays(today, 7), "yyyy-MM-dd"),
			endDate: format(today, "yyyy-MM-dd"),
			data: "7",
			filterField: "documento",
			limit: 10,
			clientFilter: "",
		};
	}, []);

	const updateSearchParams = (
		setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void,
		newParams: Record<string, string | number | undefined>
	) => {
		const currentParams = new URLSearchParams(window.location.search); // Obtém os parâmetros atuais da URL
		// Itera sobre os novos parâmetros e os adiciona ou substitui nos parâmetros existentes
		Object.entries(newParams).forEach(([key, value]) => {
			if (value !== undefined) {
				currentParams.set(key, value.toString());
			}
		});
		currentParams.set("pagina", "1"); // Reseta sempre para a página 1
		setSearchParams(currentParams, { replace: true }); // Atualiza os parâmetros de busca
	};

	const busca = useMemo(() => {
		return searchParams.get("busca") || "";
	}, [searchParams]);

	const pagina = useMemo(() => {
		return Number(searchParams.get("pagina") || "1");
	}, [searchParams]);

	const tipoAnalise = useMemo(() => {
		return searchParams.get("tipo") || "";
	}, [searchParams]);

	const statusAnalise = useMemo(() => {
		return searchParams.get("status") || "";
	}, [searchParams]);

	const origemAnalise = useMemo(() => {
		return searchParams.get("origem") || "";
	}, [searchParams]);

	const dataIniAnalise = useMemo(() => {
		return searchParams.get("dataini") || filterInitialParams.beginDate;
	}, [searchParams]);

	const dataFimAnalise = useMemo(() => {
		return searchParams.get("datafim") || filterInitialParams.endDate;
	}, [searchParams]);

	const clientFilter = useMemo(() => {
		return searchParams.get("clientFilter") || "";
	}, [searchParams]);

	const managerId = useMemo(() => {
		return searchParams.get("managerId") || "";
	}, [searchParams]);

	const limit = useMemo(() => {
		return searchParams.get("limit") || "10";
	}, [searchParams]);
	const rowsPerPage = useMemo(() => Number(limit) || 10, [limit]);

	const handleBusca = () => {
		setSearchParams(
			{
				busca,
				pagina: String(1),
				tipo: tipoAnalise,
				status: statusAnalise,
				origem: origemAnalise,
				dataini: dataIniAnalise,
				datafim: dataFimAnalise,
				clientFilter: clientFilter,
				limit: limit,
				managerId,
			},
			{ replace: true }
		);
		setPesquisar(!pesquisar);
	};

	useEffect(() => {
		setSearchParams(
			{
				...searchParams,
				dataini: filterInitialParams.beginDate,
				datafim: filterInitialParams.endDate,
				tipo: filterInitialParams.filterField,
				status: filterInitialParams.status,
				origemAnalise: filterInitialParams.createFrom,
				limit: filterInitialParams.limit.toString(),
			},
			{ replace: true }
		);
	}, []);

	useEffect(() => {
		if (hasViewPermission) {
			setIsLoading(true);
			debounce(() => {
				analysisService
					.getAll(pagina, busca, tipoAnalise, origemAnalise, statusAnalise, dataIniAnalise, dataFimAnalise, clientFilter, managerId, Number(limit))
					.then((result) => {
						setIsLoading(false);
						if (result instanceof Error) {
							toastHandler.present({
								type: "error",
								position: "top-right",
								messageOrContent: <ToastContent title={"Alerta!"} content={"Erro: " + result?.message} />,
							});
						} else {
							setTotalCount(result.totalCount);
							setRows(result.data);
						}
					})
					.catch((err: any) => {
						setIsLoading(false);
						const { titleMessage, contentMessage } = returnMessageError(err);
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					});
			});
		}
	}, [pagina, statusAnalise, pesquisar, limit]);

	const URL = window.location.protocol + "//" + window.location.host;

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<LayoutBaseDePagina
			titulo="Análises"
			subtitulo="Listagem de análises"
			barraDeFerramentas={
				<ToolbarAnalysis
					showInputMananger={true}
					showInputClient={true}
					showCreatedFrom={true}
					showInputDate={true}
					showBeetweenDate={true}
					showButtonNew={
						!isLoading &&
							(isSuperAdmin || checkByProfileGuard(
								PermissionRolesEnum.AnalysisOptions,
								PermissionActionsEnum.Add
							))
							? true
							: false
					}
					textButtonNew="Nova Análise"
					showInputSearch={true}
					onClickButtonNew={() => navigate("/analises/pedido")}
					onclickSearch={handleBusca}
					filterInitialParams={filterInitialParams}
					onChangeCreatedFrom={(retorno: string) =>
						setSearchParams(
							{
								busca,
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: retorno,
								dataini: dataIniAnalise,
								datafim: dataFimAnalise,
								clientFilter: clientFilter,
								limit: limit,
								managerId,
							},
							{ replace: true }
						)
					}
					onChangeInputClient={(retorno: string) =>
						setSearchParams(
							{
								busca,
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: dataIniAnalise,
								datafim: dataFimAnalise,
								clientFilter: retorno,
								limit: limit,
								managerId,
							},
							{ replace: true }
						)
					}
					onChangeInputMananger={(retorno: string) =>
						setSearchParams(
							{
								busca,
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: dataIniAnalise,
								datafim: dataFimAnalise,
								clientFilter: clientFilter,
								managerId: retorno,
								limit: limit,
							},
							{ replace: true }
						)
					}
					onChangeFilterField={(retorno: string) =>
						setSearchParams(
							{
								busca,
								pagina: String(1),
								tipo: retorno,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: dataIniAnalise,
								datafim: dataFimAnalise,
								clientFilter: clientFilter,
								managerId,
								limit: limit,
							},
							{ replace: true }
						)
					}
					onChangeTextSearch={(retorno: string) => {
						setSearchParams(
							{
								busca: retorno,
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: dataIniAnalise,
								datafim: dataFimAnalise,
								clientFilter: clientFilter,
								managerId,
								limit: limit,
							},
							{ replace: true }
						);
					}}
					onClickClear={() => {
						setSearchParams(
							{
								busca: "",
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: dataIniAnalise,
								datafim: dataFimAnalise,
								clientFilter: clientFilter,
								managerId,
								limit: limit,
							},
							{ replace: true }
						);
					}}
					onChangeDataIni={(retorno: string) => {
						setSearchParams(
							{
								busca,
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: retorno,
								datafim: dataFimAnalise,
								clientFilter: clientFilter,
								managerId,
								limit: limit,
							},
							{ replace: true }
						);
					}}
					onChangeDataFim={(retorno: string) => {
						setSearchParams(
							{
								busca,
								pagina: String(1),
								tipo: tipoAnalise,
								status: statusAnalise,
								origem: origemAnalise,
								dataini: dataIniAnalise,
								datafim: retorno,
								clientFilter: clientFilter,
								managerId,
								limit: limit,
							},
							{ replace: true }
						);
					}}
				/>
			}
		>
			{hasViewPermission && (
				<TableContainer
					component={Paper}
					variant="elevation"
					sx={{
						mt: 1,
						width: "96%",
						px: { xs: 1, sm: 3 },
						py: { xs: 2, sm: 3 },
						ml: 1,
						borderRadius: 2,
						overflow: "hidden",
					}}
				>
					<Box mb={2}>
						<ToolbarGridAnalises
							onClickTodos={() => updateSearchParams(setSearchParams, { status: "Todos" })}
							onClickPreAprovadas={() => updateSearchParams(setSearchParams, { status: "pre_approved" })}
							onClickEmContratacao={() => updateSearchParams(setSearchParams, { status: "in_hiring" })}
							onClickAprovadas={() => updateSearchParams(setSearchParams, { status: "issued" })}
							onClickExpiradas={() => updateSearchParams(setSearchParams, { status: "expired" })}
							onClickRecusadas={() => updateSearchParams(setSearchParams, { status: "refused" })}
							onClickPendentes={() => updateSearchParams(setSearchParams, { status: "awaiting_signature" })}
							onClickBiometria={() => updateSearchParams(setSearchParams, { status: "awaiting_biometria" })}
							onClickCancel={() => updateSearchParams(setSearchParams, { status: "canceled" })}
							selectTodos={searchParams.get("status") === "Todos"}
							selectPreAprovada={searchParams.get("status") === "pre_approved"}
							selectEmContratacao={searchParams.get("status") === "in_hiring"}
							selectAprovada={searchParams.get("status") === "issued"}
							selectExpirada={searchParams.get("status") === "expired"}
							selectRecusada={searchParams.get("status") === "refused"}
							selectPendente={searchParams.get("status") === "awaiting_signature"}
							selectBiometria={searchParams.get("status") === "awaiting_biometria"}
							selectCancel={searchParams.get("status") === "canceled"}
						/>
					</Box>

					<Table size="small" stickyHeader aria-label="a dense table">
						<TableHead>
							<TableRow sx={{ backgroundColor: "grey.100" }}>
								<TableCell>
									<Typography variant="button">Nome</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Parceiro</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Endereço</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Status</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Cadastro</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Origem</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Ações</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.length === 0 && !isLoading && (
								<TableRow>
									<TableCell colSpan={7} align="center">
										Nenhum registro
									</TableCell>
								</TableRow>
							)}

							{!isLoading &&
								rows.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell sx={{ fontWeight: "normal" }}>
											{row.name}
											<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
												{globalMaskDocument(row.document)}
											</Typography>
										</TableCell>
										<TableCell>
											<HtmlTooltip
												title={
													<Box>
														<Typography variant="subtitle2" fontWeight="bold">
															{row.client?.name}
															{row.client?.status == "inactive" && " (Inativo)"}
														</Typography>
													</Box>
												}
												arrow
												placement="top"
											>
												<Box>
													<Typography
														variant="subtitle2"
														color={"#5c5858"}
														sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: "normal" }}
													>
														{row.client?.name}
														{row.client?.status == "inactive" && " (Inativo)"}
													</Typography>
												</Box>
											</HtmlTooltip>
										</TableCell>

										<TableCell
											sx={{
												color: "#959595",
												fontWeight: "normal",
											}}
										>
											{row.addressStreet != null && row.addressStreet !== "" ? (
												<HtmlTooltip
													title={
														<Box>
															<Typography variant="subtitle2" fontWeight="bold">
																Endereço completo
															</Typography>
															<Typography variant="body2">
																{row.addressComplement != null && row.addressComplement !== ""
																	? `${row.addressStreet}, ${row.addressNumber} - ${row.addressComplement} - ${row.addressDistrict}`
																	: `${row.addressStreet}, ${row.addressNumber} - ${row.addressDistrict}`}
															</Typography>
															<Typography variant="body2">
																{row.addressCity != null && row.addressCity !== "" ? `${row.addressCity} - ${row.addressState}` : "Cidade não informada"}
															</Typography>
														</Box>
													}
													arrow
													placement="top"
												>
													<Box>
														<Typography
															variant="subtitle2"
															color={"#959595"}
															fontWeight={"normal"}
															noWrap
															sx={{ maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis" }}
														>
															{row.addressComplement != null && row.addressComplement !== ""
																? `${row.addressStreet}, ${row.addressNumber} - ${row.addressComplement} - ${row.addressDistrict}`
																: `${row.addressStreet}, ${row.addressNumber} - ${row.addressDistrict}`}
														</Typography>
														<Typography
															variant="subtitle2"
															color={"#959595"}
															fontWeight={"normal"}
															noWrap
															sx={{ maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis" }}
														>
															{row.addressCity != null && row.addressCity !== "" ? `${row.addressCity} - ${row.addressState}` : ""}
														</Typography>
													</Box>
												</HtmlTooltip>
											) : (
												<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
													Endereço não cadastrado
												</Typography>
											)}
										</TableCell>
										<TableCell sx={{ textAlign: "start" }}>
											<AnalysisStatusChip status={row.status} />
											{(row.status == 'pending') && (
												<Tooltip title="Reenviar Analise">


													<IconButton
														size="small"
														onClick={() => analysisService.resendAnalysis(row.id, '4').then((result) => {
															if (result instanceof Error) {
																toastHandler.present({
																	type: "error",
																	position: "top-right",
																	messageOrContent: <ToastContent title={"Alerta!"} content={"Erro: " + result?.message} />,
																});
															} else {
																toastHandler.present({
																	type: "success",
																	position: "top-right",
																	messageOrContent: <ToastContent title={"Sucesso!"} content={"Análise reenviada com sucesso!"} />,
																});
															}
														}
														)}
														sx={{ borderRadius: "18px" }}
													>
														<Icon
															sx={{
																color: "#0170B3",
																fontWeight: "normal",
															}}
														>
															{" "}
															sync_arrow_up
														</Icon>
														{/* <Typography
													variant="subtitle2"
													marginLeft={1}
													sx={{
														color: "#0170B3",
														fontWeight: "normal",
													}}
												>
													Reenviar
												</Typography> */}
													</IconButton>
												</Tooltip>
											)}
										</TableCell>
										<TableCell>
											<Typography
												variant="subtitle2"
												color={"#959595"}
												fontWeight={"normal"}
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
												}}
											>
												{row.setup_amount != null && (
													<Tooltip title={`Taxa de setup R$  ${globalMaskReal(row?.setup_amount)}`}>
														<CreditCardOutlined />
													</Tooltip>
												)}

												{row?.createdAt ? (
													<HtmlTooltip
														title={
															<Box>
																<Typography variant="subtitle2" fontWeight="bold">
																	Cadastro
																</Typography>
																<Typography variant="body2">
																	{format(new Date(row.createdAt), "dd/MM/yyyy HH:mm:ss")}
																</Typography>
															</Box>
														}
														arrow
														placement="top"
													>
														<Box component="span">{format(new Date(row.createdAt), "dd/MM/yyyy")}</Box>
													</HtmlTooltip>
												) : (
													"Não informado"
												)}
											</Typography>
											{(row.status == "issued" || row.status == "awaiting_signature" || row.status == "awaiting_biometria") && (
												<HtmlTooltip
													title={
														<Box>
															<Typography variant="subtitle2" fontWeight="bold">
																Expira em:
															</Typography>
															<Typography variant="body2">
																{row.expiresSignature
																	? format(new Date(row.expiresSignature), "dd/MM/yyyy")
																	: format(addDays(new Date(row.createdAt), 30), "dd/MM/yyyy")}
															</Typography>
														</Box>
													}
													arrow
													placement="top"
												>
													<Box component="span">
														<Typography
															variant="subtitle2"
															display="block"
															textAlign={"center"}
															fontWeight={"normal"}
															sx={{ mt: 0.5, color: "#e48509" }}
														>
															{row.expiresSignature
																? format(new Date(row.expiresSignature), "dd/MM/yyyy")
																: format(addDays(new Date(row.createdAt), 30), "dd/MM/yyyy")}
														</Typography>
													</Box>
												</HtmlTooltip>
											)}
										</TableCell>
										<TableCell sx={{ textAlign: "start" }}>
											{(() => {
												const createdFrom = String(row.createdFrom || "").toLowerCase();
												const isWebApp = createdFrom === "web_app" || createdFrom === "web" || createdFrom === "webapp";
												const title = isWebApp ? "Web Aplicação" : "Integração";
												const src = isWebApp ? "/logogar.png" : "/4seg.png";
												const width = isWebApp ? 30 : 40;

												return (
													<Tooltip title={title}>
														<Box
															component="span"
															sx={{
																display: "inline-flex",
																alignItems: "center",
																justifyContent: "center",
																minWidth: 40,
															}}
														>
															<Box component="img" src={src} width={width} sx={{ height: "auto" }} />
														</Box>
													</Tooltip>
												);
											})()}
										</TableCell>
										<TableCell sx={{ textAlign: "center" }}>
											<IconButton
												size="small"
												onClick={() => window.open(`${URL}/analises/visualizar/${row.id}`)}
												sx={{ borderRadius: "18px" }}
											>
												<Icon
													sx={{
														color: "#0170B3",
														fontWeight: "normal",
													}}
												>
													{" "}
													crop_free_OutlinedIcon
												</Icon>
												<Typography
													variant="subtitle2"
													marginLeft={1}
													sx={{
														color: "#0170B3",
														fontWeight: "normal",
													}}
												>
													Abrir
												</Typography>
											</IconButton>
										</TableCell>
									</TableRow>
								))}
						</TableBody>
						{totalCount === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}
					</Table>
					<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
						<TablePagination
							component="div"
							count={totalCount}
							page={Math.max(pagina - 1, 0)}
							rowsPerPage={rowsPerPage}
							rowsPerPageOptions={[5, 10, 25, 50]}
							onPageChange={(_, newPage) =>
								setSearchParams(
									{
										busca,
										pagina: String(newPage + 1),
										tipo: tipoAnalise,
										status: statusAnalise,
										origem: origemAnalise,
										dataini: dataIniAnalise,
										datafim: dataFimAnalise,
										clientFilter: clientFilter,
										limit,
										managerId,
									},
									{ replace: true }
								)
							}
							onRowsPerPageChange={(event) =>
								setSearchParams(
									{
										busca,
										pagina: "1",
										tipo: tipoAnalise,
										status: statusAnalise,
										origem: origemAnalise,
										dataini: dataIniAnalise,
										datafim: dataFimAnalise,
										clientFilter: clientFilter,
										limit: event.target.value,
										managerId,
									},
									{ replace: true }
								)
							}
							labelRowsPerPage="Linhas por página:"
							labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
						/>
					</Box>
				</TableContainer>
			)}
			</LayoutBaseDePagina>
		</>
	);
};
